import api from '@/lib/api';
import type { PaginatedResponse } from '@pms/shared';

// ─── Payment type ────────────────────────────────────────────────────────────

/**
 * How a booking will be settled. `null` on a booking means the front-desk has
 * not classified it yet. (String-literal union until a shared enum exists.)
 */
export type PaymentType = 'prepaid_ota' | 'pay_at_hotel' | 'bill_to_company';

// ─── Query interfaces ────────────────────────────────────────────────────────

export interface FrontDeskDateQuery {
  date?: string | undefined;
  propertyId: string;
}

export interface InHouseQuery {
  propertyId: string;
}

export interface SearchReservationsQuery {
  guestName?: string | undefined;
  bookingId?: string | undefined;
  propertyId?: string | undefined;
  status?: string | undefined;
  source?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

// ─── Response interfaces ─────────────────────────────────────────────────────

/** Booking entity with joined guest and unit as returned by the backend */
export interface BookingEntry {
  id: string;
  unitId: string;
  guestId: string | null;
  source: string;
  externalBookingId: string | null;
  status: string;
  checkIn: string;
  checkOut: string;
  guestsCount: number;
  /** OTA-standard party composition (from public-site bookings). */
  adults?: number | null;
  children?: number | null;
  /** Names of accompanying guests (primary guest is `guest.fullName`). */
  guestNames?: string | null;
  /** Estimated arrival window label, e.g. "3:00–6:00 PM". */
  arrivalTime?: string | null;
  baseAmount: string;
  totalAmount: string;
  currency: string;
  /** How the booking will be settled; null = not yet classified. */
  paymentType?: PaymentType | null;
  /**
   * The full raw payload the channel manager (AxisRooms API-9) pushed for this
   * booking, retained verbatim so the reservation detail can surface every
   * field the OTA sent. `null`/absent for direct & storefront bookings.
   */
  channelPayload?: Record<string, unknown> | null;
  specialRequests: string | null;
  createdAt: string;
  updatedAt: string;
  guest: {
    id: string;
    fullName: string;
    email: string | null;
    phone: string | null;
  } | null;
  unit: {
    id: string;
    name: string;
    propertyId: string;
    roomTypeId: string | null;
    type: string;
  } | null;
}

// Legacy type aliases kept for backward compatibility with FrontDeskPage
export type ArrivalEntry = BookingEntry;
export type DepartureEntry = BookingEntry;
export type InHouseEntry = BookingEntry;

// The old wrapper types are not used by the backend; kept here as documentation
// of the intended future format, but not referenced at runtime.
export interface ArrivalsResponse {
  data: ArrivalEntry[];
  summary: {
    expected: number;
    checkedIn: number;
    noShow: number;
    total: number;
  };
}

export interface DeparturesResponse {
  data: DepartureEntry[];
  summary: {
    expected: number;
    checkedOut: number;
    extended: number;
    total: number;
  };
}

export interface InHouseResponse {
  data: InHouseEntry[];
  summary: {
    totalInHouse: number;
    occupancyPercentage: number;
  };
}

// ─── API functions ───────────────────────────────────────────────────────────

/** Backend returns flat Booking[] for arrivals */
export async function fetchArrivals(query: FrontDeskDateQuery): Promise<BookingEntry[]> {
  const { data } = await api.get<BookingEntry[]>('/frontdesk/arrivals', { params: query });
  return data;
}

/** Backend returns flat Booking[] for departures */
export async function fetchDepartures(query: FrontDeskDateQuery): Promise<BookingEntry[]> {
  const { data } = await api.get<BookingEntry[]>('/frontdesk/departures', { params: query });
  return data;
}

/** Backend returns flat Booking[] for in-house */
export async function fetchInHouse(query: InHouseQuery): Promise<BookingEntry[]> {
  const { data } = await api.get<BookingEntry[]>('/frontdesk/in-house', { params: query });
  return data;
}

/** Backend returns PaginatedResponse<Booking> for search */
export async function searchReservations(query: SearchReservationsQuery): Promise<PaginatedResponse<BookingEntry>> {
  const { data } = await api.get<PaginatedResponse<BookingEntry>>('/frontdesk/search', { params: query });
  return data;
}

/**
 * Set (or clear, with `null`) a booking's payment type.
 * `PATCH /v1/bookings/:id/payment-type` → the updated booking.
 */
export async function updateBookingPaymentType(
  id: string,
  paymentType: PaymentType | null,
): Promise<BookingEntry> {
  const { data } = await api.patch<BookingEntry>(`/bookings/${id}/payment-type`, { paymentType });
  return data;
}
